<template>
  <section class="space-y-5">
    <header>
      <h2 class="text-lg font-semibold text-gray-900">Precision Analysis</h2>
      <p class="text-sm text-gray-500">Three bullet columns plus enrollment CTA.</p>
    </header>

    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Eyebrow</label>
        <input
          v-model="content.eyebrow"
          type="text"
          maxlength="120"
          class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
        >
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Headline</label>
        <input
          v-model="content.headline"
          type="text"
          maxlength="250"
          class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
        >
      </div>
    </div>

    <TemplateRichTextInput
      v-model="content.intro"
      label="Intro paragraph"
      :max-length="3000"
    />

    <div v-for="column in columns" :key="column.key">
      <label class="mb-2 block text-sm font-medium text-gray-700">{{ column.label }}</label>
      <TemplateRepeatableList
        v-model="content[column.key]"
        :max-items="12"
        :new-item="newBullet"
        item-noun="bullet"
      >
        <template #item="{ item, index }">
          <input
            :value="unwrap(item)"
            type="text"
            maxlength="500"
            :placeholder="`Bullet ${index + 1}`"
            class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
            @input="updateBullet(column.key, index, $event.target.value)"
          >
        </template>
      </TemplateRepeatableList>
    </div>

    <div class="rounded-md border border-pink-200 bg-pink-50/30 p-4">
      <h3 class="mb-3 text-sm font-semibold text-gray-800">Enrollment Card (on the campaign page)</h3>
      <div class="space-y-3">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Headline</label>
          <input
            v-model="content.enrollment_cta.headline"
            type="text"
            maxlength="250"
            class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
          >
        </div>

        <TemplateRichTextInput
          v-model="content.enrollment_cta.body"
          label="Body"
          :max-length="3000"
        />

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">Card benefits (bullet list)</label>
          <TemplateRepeatableList
            v-model="content.enrollment_cta.benefits"
            :max-items="8"
            :new-item="newBullet"
            item-noun="benefit"
          >
            <template #item="{ item, index }">
              <input
                :value="unwrap(item)"
                type="text"
                maxlength="200"
                :placeholder="`Benefit ${index + 1}`"
                class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
                @input="updateBenefit(index, $event.target.value)"
              >
            </template>
          </TemplateRepeatableList>
        </div>

        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Button label</label>
          <input
            v-model="content.enrollment_cta.button_label"
            type="text"
            maxlength="120"
            class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
          >
        </div>
      </div>
    </div>

    <div class="rounded-md border border-indigo-200 bg-indigo-50/30 p-4">
      <h3 class="mb-1 text-sm font-semibold text-gray-800">Enrollment Modal</h3>
      <p class="mb-3 text-xs text-gray-500">
        Copy for the left-hand panel of the popup that opens when a visitor clicks "Request Enrollment". Leave blank to use the defaults.
      </p>
      <div class="space-y-3">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">Eyebrow badge</label>
            <input
              v-model="content.enrollment_cta.modal_badge"
              type="text"
              maxlength="120"
              placeholder="PATIENT RESEARCH PROGRAM"
              class="w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">Modal headline</label>
            <input
              v-model="content.enrollment_cta.modal_headline"
              type="text"
              maxlength="250"
              placeholder="Join Our Precision Medicine Study"
              class="w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
          </div>
        </div>

        <TemplateRichTextInput
          v-model="content.enrollment_cta.modal_body"
          label="Modal body"
          :max-length="3000"
        />

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">Benefit pillars (up to 3, shown in the modal)</label>
          <TemplateRepeatableList
            v-model="content.enrollment_cta.modal_benefits"
            :max-items="3"
            :new-item="newModalBenefit"
            item-noun="pillar"
          >
            <template #item="{ item, index }">
              <div class="space-y-2">
                <input
                  v-model="item.title"
                  type="text"
                  maxlength="120"
                  :placeholder="`Pillar ${index + 1} title`"
                  class="w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                <input
                  v-model="item.body"
                  type="text"
                  maxlength="200"
                  :placeholder="`Pillar ${index + 1} description`"
                  class="w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
              </div>
            </template>
          </TemplateRepeatableList>
        </div>
      </div>
    </div>

    <details class="rounded-md border border-gray-200 bg-gray-50/50 p-3 text-xs">
      <summary class="cursor-pointer select-none text-gray-600">
        Legacy external URL <span class="text-gray-400">(advanced — leave blank to use the modal)</span>
      </summary>
      <div class="mt-3 space-y-2">
        <p class="text-gray-500">
          If set, the "Request Enrollment" button is treated as a legacy external link
          (the in-page modal is intentionally still used on the public site; this column
          is retained for archived campaigns that pointed to a third-party form).
        </p>
        <input
          v-model="content.enrollment_cta.button_link"
          type="text"
          maxlength="500"
          placeholder="https://…"
          class="w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
        >
      </div>
    </details>
  </section>
</template>

<script>
import TemplateRepeatableList from '../TemplateRepeatableList.vue';
import TemplateRichTextInput from '../TemplateRichTextInput.vue';

export default {
  name: 'PrecisionAnalysisSectionEditor',
  components: { TemplateRepeatableList, TemplateRichTextInput },
  props: {
    content: { type: Object, required: true },
  },
  data() {
    return {
      columns: [
        { key: 'what_you_get', label: 'What You Get' },
        { key: 'how_it_works', label: 'How It Works' },
        { key: 'who_qualifies', label: 'Who Qualifies' },
      ],
    };
  },
  methods: {
    newBullet() {
      return '';
    },
    /**
     * Repeatable-list item shape for the modal benefit pillars — each
     * pillar has its own title + supporting body.
     */
    newModalBenefit() {
      return { title: '', body: '' };
    },
    unwrap(item) {
      // Items may be bare strings or wrapper objects (see TemplateRepeatableList).
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && '_value' in item) return item._value;
      return '';
    },
    updateBullet(columnKey, index, value) {
      const next = this.content[columnKey].slice();
      next[index] = value;
      this.content[columnKey] = next;
    },
    updateBenefit(index, value) {
      const next = this.content.enrollment_cta.benefits.slice();
      next[index] = value;
      this.content.enrollment_cta.benefits = next;
    },
  },
};
</script>
